import type { Transition, Variants } from 'framer-motion';

// Shared motion language: one element morphs (size, radius, color) on springs with
// at most a tiny overshoot; content inside it swaps with a short blur.

export const MOTION_COLORS = {
  ink: '#363434',
  accent: '#E15600',
  success: '#1F8A4C',
  surface: '#FFFFFF',
  canvas: '#FFFAF7',
  track: 'rgba(233, 227, 224, 0.7)',
  muted: 'rgba(54, 52, 52, 0.58)',
  hairline: 'rgba(54, 52, 52, 0.12)',
} as const;

export const MOTION_FONT = "'Montserrat', sans-serif";

// Review aid for the motion lab (slow-motion playback). The app never changes it.
let timeScale = 1;
export const setMotionTimeScale = (scale: number) => { timeScale = scale; };
export const getMotionTimeScale = () => timeScale;

const spring = (visualDuration: number, bounce: number): Transition => ({
  type: 'spring',
  visualDuration: visualDuration * timeScale,
  bounce,
});
const tween = (duration: number, delay = 0): Transition => ({
  duration: duration * timeScale,
  delay: delay * timeScale,
  ease: [0.4, 0, 1, 1],
});

export const SPRING = {
  // Container morphs: width/height/radius/position.
  get morph() { return spring(0.38, 0.12); },
  // Small, quick settles: content enter, color, knob.
  get snap() { return spring(0.22, 0.08); },
  // Leading / trailing edges of a stretching indicator.
  get lead() { return spring(0.24, 0.1); },
  get trail() { return spring(0.42, 0.06); },
  // Press feedback: no overshoot.
  get press() { return spring(0.14, 0); },
};

export const exitTween = tween;
export const delayed = (transition: Transition, delay: number): Transition => ({
  ...transition,
  delay: delay * timeScale,
});

// Content that swaps inside a morphing container needs its own enter/exit timing:
// exit is short and eased, enter waits a beat so the two never overlap.
export const BLUR_SWAP: Variants = {
  initial: { opacity: 0, filter: 'blur(6px)', scale: 0.9 },
  get animate() {
    return {
      opacity: 1,
      filter: 'blur(0px)',
      scale: 1,
      transition: { ...delayed(SPRING.snap, 0.06), opacity: tween(0.16, 0.06) },
    };
  },
  get exit() {
    return { opacity: 0, filter: 'blur(6px)', scale: 0.9, transition: tween(0.1) };
  },
};

export const PRESSABLE = {
  whileTap: { scale: 0.96 },
  get transition() { return SPRING.press; },
};

// framer-motion 12 hands opacity/filter animations to the browser (WAAPI) and, when one
// finishes, cancels it a frame before the final value is rendered, so an element fading or
// unblurring in flashes its starting value for one frame. Any onUpdate keeps the animation on
// framer's own loop, which renders every frame. Spread on motion elements that fade or blur.
export const RENDER_EVERY_FRAME = { onUpdate: () => undefined } as const;
